import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Activity, RefreshCw, Cpu, Timer, Database, Gauge, ScrollText, AlertTriangle,
  ShieldAlert, Play, Skull, FolderPlus, CheckCircle2, Circle, Loader2, Info,
} from "lucide-react";

const ACCENT = "#8b5cf6";

// ============================================================ Activity Monitor
export function ActivityMonitor() {
  const [data, setData] = useState(null);
  const [auto, setAuto] = useState(true);
  const [loading, setLoading] = useState(true);
  const timer = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/sql/admin/activity");
      setData(data);
    } catch { /* ignore */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (auto) { timer.current = setInterval(load, 3000); }
    return () => clearInterval(timer.current);
  }, [auto]);

  if (loading && !data) return <Skeleton label="Loading Activity Monitor…" />;
  const o = data?.overview || {};
  return (
    <div className="p-4 space-y-4" data-testid="sql-activity-monitor">
      <div className="flex items-center gap-2">
        <Activity className="h-4 w-4" style={{ color: ACCENT }} />
        <h3 className="text-sm font-semibold">Activity Monitor — HMG-SQL01</h3>
        {data?.blocking_active && (
          <span className="text-[11px] px-2 py-0.5 rounded bg-rose-500/15 text-rose-500 font-medium inline-flex items-center gap-1" data-testid="am-blocking-badge">
            <ShieldAlert className="h-3 w-3" /> Blocking detected
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <label className="text-xs text-muted-foreground flex items-center gap-1.5">
            <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} data-testid="am-auto" /> Auto-refresh
          </label>
          <Button size="sm" variant="outline" className="gap-1.5" onClick={load} data-testid="am-refresh"><RefreshCw className="h-3.5 w-3.5" /> Refresh</Button>
        </div>
      </div>

      {/* Overview tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile icon={Cpu} label="% Processor Time" value={`${o.processor_time_pct ?? 0}%`} warn={(o.processor_time_pct ?? 0) > 50} />
        <Tile icon={Timer} label="Waiting Tasks" value={o.waiting_tasks ?? 0} warn={(o.waiting_tasks ?? 0) > 0} />
        <Tile icon={Database} label="Database I/O (MB/s)" value={o.database_io_mb_sec ?? 0} />
        <Tile icon={Gauge} label="Batch Requests/sec" value={o.batch_requests_sec ?? 0} />
      </div>

      {/* Processes */}
      <Section title="Processes">
        <table className="w-full text-[11px]" data-testid="am-processes">
          <thead className="bg-muted"><tr>{["SPID", "Login", "Database", "Status", "Command", "Wait Type", "Wait (ms)", "Blocked By", "Application"].map((h) => <th key={h} className="text-left px-2 py-1.5 font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
          <tbody>
            {(data?.processes || []).map((p) => (
              <tr key={p.spid} data-testid={`am-proc-${p.spid}`}
                className={`border-t border-border ${p.head_blocker ? "bg-rose-500/10" : p.blocked_by ? "bg-amber-500/10" : ""}`}>
                <td className="px-2 py-1 font-mono font-semibold">{p.spid}{p.head_blocker && <span className="ml-1 text-[9px] text-rose-500">HEAD</span>}</td>
                <td className="px-2 py-1 whitespace-nowrap">{p.login}</td>
                <td className="px-2 py-1">{p.database}</td>
                <td className="px-2 py-1">{p.status}</td>
                <td className="px-2 py-1">{p.command}</td>
                <td className="px-2 py-1 font-mono">{p.wait_type || "—"}</td>
                <td className="px-2 py-1 text-right">{p.wait_time_ms ? p.wait_time_ms.toLocaleString() : "—"}</td>
                <td className={`px-2 py-1 font-mono ${p.blocked_by ? "text-amber-500 font-semibold" : "text-muted-foreground"}`}>{p.blocked_by || "0"}</td>
                <td className="px-2 py-1 whitespace-nowrap text-muted-foreground">{p.application}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      <div className="grid md:grid-cols-2 gap-4">
        <Section title="Resource Waits">
          <table className="w-full text-[11px]">
            <thead className="bg-muted"><tr>{["Category", "Wait Time (s)", "Recent Wait (ms)", "Waiters"].map((h) => <th key={h} className="text-left px-2 py-1.5 font-semibold">{h}</th>)}</tr></thead>
            <tbody>{(data?.resource_waits || []).map((w) => (
              <tr key={w.category} className="border-t border-border"><td className="px-2 py-1">{w.category}</td><td className="px-2 py-1">{w.wait_time_sec}</td><td className="px-2 py-1">{w.recent_wait_ms.toLocaleString()}</td><td className="px-2 py-1">{w.waiters}</td></tr>
            ))}</tbody>
          </table>
        </Section>
        <Section title="Data File I/O">
          <table className="w-full text-[11px]">
            <thead className="bg-muted"><tr>{["Database", "File", "MB/s Read", "MB/s Written", "Resp (ms)"].map((h) => <th key={h} className="text-left px-2 py-1.5 font-semibold">{h}</th>)}</tr></thead>
            <tbody>{(data?.data_file_io || []).map((f) => (
              <tr key={f.file} className="border-t border-border"><td className="px-2 py-1">{f.database}</td><td className="px-2 py-1 font-mono">{f.file}</td><td className="px-2 py-1">{f.mb_read_sec}</td><td className="px-2 py-1">{f.mb_written_sec}</td><td className="px-2 py-1">{f.response_ms}</td></tr>
            ))}</tbody>
          </table>
        </Section>
      </div>

      <Section title="Active Expensive Queries">
        <table className="w-full text-[11px]" data-testid="am-expensive">
          <thead className="bg-muted"><tr>{["SPID", "Elapsed (s)", "Executions", "CPU (ms)", "Reads", "Query Text"].map((h) => <th key={h} className="text-left px-2 py-1.5 font-semibold">{h}</th>)}</tr></thead>
          <tbody>{(data?.expensive_queries || []).map((q, i) => (
            <tr key={i} className="border-t border-border"><td className="px-2 py-1 font-mono">{q.spid}</td><td className="px-2 py-1">{q.elapsed_sec}</td><td className="px-2 py-1">{q.executions}</td><td className="px-2 py-1">{q.cpu_ms}</td><td className="px-2 py-1">{q.reads.toLocaleString()}</td><td className="px-2 py-1 font-mono text-muted-foreground truncate max-w-md">{q.text}</td></tr>
          ))}</tbody>
        </table>
      </Section>
    </div>
  );
}

// ============================================================ Error Log Viewer
export function ErrorLogViewer() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    try { const { data } = await api.get("/sql/admin/errorlog"); setData(data); }
    catch { /* ignore */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  if (loading && !data) return <Skeleton label="Loading SQL Server Error Log…" />;
  return (
    <div className="p-4 space-y-3" data-testid="sql-error-log">
      <div className="flex items-center gap-2">
        <ScrollText className="h-4 w-4" style={{ color: ACCENT }} />
        <h3 className="text-sm font-semibold">SQL Server Logs — Current</h3>
        <Button size="sm" variant="outline" className="gap-1.5 ml-auto" onClick={load} data-testid="errorlog-refresh"><RefreshCw className="h-3.5 w-3.5" /> Refresh</Button>
      </div>
      <div className="rounded border border-border overflow-hidden">
        <table className="w-full text-[11px]">
          <thead className="bg-muted"><tr>{["Date", "Source", "Severity", "Message"].map((h) => <th key={h} className="text-left px-2 py-1.5 font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
          <tbody>
            {(data?.entries || []).map((e, i) => (
              <tr key={i} className={`border-t border-border ${e.severity >= 16 ? "bg-rose-500/10" : ""}`} data-testid={`errorlog-row-${i}`}>
                <td className="px-2 py-1 whitespace-nowrap font-mono text-muted-foreground">{new Date(e.date).toLocaleString()}</td>
                <td className="px-2 py-1 font-mono">{e.source}</td>
                <td className={`px-2 py-1 font-semibold ${e.severity >= 16 ? "text-rose-500" : "text-muted-foreground"}`}>{e.severity}</td>
                <td className="px-2 py-1">{e.severity >= 16 && <AlertTriangle className="h-3 w-3 text-rose-500 inline mr-1 -mt-0.5" />}{e.message}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================ DBA Scenarios
export function DbaScenarios({ onStateChange }) {
  const [cat, setCat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [spid, setSpid] = useState({});

  const load = async () => {
    try { const { data } = await api.get("/sql/admin/incidents"); setCat(data); }
    catch { /* ignore */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const start = async (id) => {
    setBusy(id);
    try {
      const { data } = await api.post(`/sql/admin/incidents/${id}/start`);
      setCat(data); toast.success("Scenario started — investigate the symptoms, then remediate.");
      onStateChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start scenario."); }
    finally { setBusy(null); }
  };

  const act = async (id, action, extra = {}) => {
    setBusy(id + action);
    try {
      const { data } = await api.post(`/sql/admin/incidents/${id}/action`, { action, ...extra });
      if (data.resolved) toast.success(data.message);
      else if (data.ok) toast.success(data.message);
      else toast.error(data.message);
      await load();
      onStateChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed."); }
    finally { setBusy(null); }
  };

  if (loading && !cat) return <Skeleton label="Loading DBA scenarios…" />;
  const agentStopped = cat?.agent_status !== "Running";

  return (
    <div className="p-4 space-y-4" data-testid="sql-dba-scenarios">
      <div className="flex items-start gap-2 text-xs text-muted-foreground rounded-md border border-border bg-card p-3">
        <Info className="h-4 w-4 shrink-0 mt-0.5" style={{ color: ACCENT }} />
        <span>Guided incident response. Start a scenario to inject a real fault into <strong>your</strong> instance, then diagnose it via Activity Monitor / Error Log / job history and remediate it. Each incident resolves only when you take the correct action.</span>
      </div>

      {(cat?.incidents || []).map((inc) => {
        const started = inc.active;
        const done = inc.resolved;
        return (
          <div key={inc.id} className="rounded-lg border border-border bg-card overflow-hidden" data-testid={`scenario-${inc.id}`}>
            <div className="p-4 flex items-start gap-3">
              {done ? <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" /> : <Circle className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm">{inc.title}</span>
                  <span className="text-[11px] px-2 py-0.5 rounded bg-secondary text-secondary-foreground">{inc.difficulty}</span>
                  <span className="text-[11px] px-2 py-0.5 rounded bg-muted text-muted-foreground">{inc.category}</span>
                  {done && <span className="text-[11px] px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-500 font-medium">Resolved</span>}
                  {started && !done && <span className="text-[11px] px-2 py-0.5 rounded bg-amber-500/15 text-amber-600 font-medium">Active</span>}
                </div>
                <p className="text-sm text-muted-foreground mt-1">{inc.summary}</p>

                {!started ? (
                  <Button size="sm" className="gap-1.5 mt-3" disabled={busy === inc.id} data-testid={`scenario-start-${inc.id}`}
                    style={{ background: ACCENT, color: "#fff" }} onClick={() => start(inc.id)}>
                    {busy === inc.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />} Start scenario
                  </Button>
                ) : (
                  <div className="mt-3 space-y-3">
                    <div>
                      <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Symptoms</div>
                      <ul className="list-disc pl-5 space-y-0.5 text-xs">{inc.symptoms.map((s, i) => <li key={i}>{s}</li>)}</ul>
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">How to investigate</div>
                      <ul className="list-disc pl-5 space-y-0.5 text-xs text-muted-foreground">{inc.investigate.map((s, i) => <li key={i}>{s}</li>)}</ul>
                    </div>
                    {!done && (
                      <div>
                        <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5">Remediation</div>
                        <div className="flex flex-wrap items-center gap-2">
                          {inc.actions.map((a) => a.input === "spid" ? (
                            <div key={a.id} className="flex items-center gap-2">
                              <Input placeholder="SPID" value={spid[inc.id] || ""} onChange={(e) => setSpid((s) => ({ ...s, [inc.id]: e.target.value }))}
                                className="h-8 w-24 text-xs" data-testid={`scenario-spid-${inc.id}`} />
                              <Button size="sm" variant="destructive" className="gap-1.5" disabled={busy === inc.id + a.id}
                                data-testid={`scenario-action-${inc.id}-${a.id}`} onClick={() => act(inc.id, a.id, { spid: spid[inc.id] })}>
                                <Skull className="h-3.5 w-3.5" /> {a.label}
                              </Button>
                            </div>
                          ) : (
                            <Button key={a.id} size="sm" variant={a.icon === "warn" ? "outline" : "default"} className="gap-1.5"
                              disabled={busy === inc.id + a.id} data-testid={`scenario-action-${inc.id}-${a.id}`}
                              style={a.icon === "warn" ? {} : { background: ACCENT, color: "#fff" }}
                              onClick={() => act(inc.id, a.id)}>
                              {busy === inc.id + a.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : a.icon === "folder" ? <FolderPlus className="h-3.5 w-3.5" /> : a.icon === "warn" ? <AlertTriangle className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                              {a.label}
                            </Button>
                          ))}
                        </div>
                        {inc.id === "inc-blocking" && !agentStopped && (
                          <p className="text-[11px] text-muted-foreground mt-2">Open the <strong>Activity Monitor</strong> tab to find the head-blocker SPID (the one others are Blocked By).</p>
                        )}
                      </div>
                    )}
                    {(inc.events || []).length > 0 && (
                      <div className="rounded border border-border bg-muted/30 p-2" data-testid={`scenario-events-${inc.id}`}>
                        <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Activity log</div>
                        <ul className="space-y-0.5 text-[11px] font-mono">
                          {inc.events.map((ev, i) => <li key={i} className="text-muted-foreground"><span className="text-foreground/60">{new Date(ev.at).toLocaleTimeString()}</span> — {ev.message}</li>)}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------- shared primitives ----------------
function Tile({ icon: Icon, label, value, warn }) {
  return (
    <div className={`rounded-lg border p-3 ${warn ? "border-amber-500/40 bg-amber-500/5" : "border-border bg-card"}`}>
      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground"><Icon className="h-3.5 w-3.5" /> {label}</div>
      <div className={`text-2xl font-bold mt-1 ${warn ? "text-amber-500" : ""}`}>{value}</div>
    </div>
  );
}
function Section({ title, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5">{title}</div>
      <div className="rounded border border-border overflow-x-auto">{children}</div>
    </div>
  );
}
function Skeleton({ label }) {
  return (
    <div className="p-8 flex items-center gap-2 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" /> {label}
    </div>
  );
}
